// ⚠️ Pages Router only — use useAppDirectoryStableQueryParams for App Router pages
// TODO: Once fully moved to App Directory, delete this implementation
import { ParsedUrlQuery } from 'querystring'

import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { useStableCallback } from '@syconium/little-miss-figgy'

import {
  UpdateQueryParamsOptions,
  UseQueryParams,
} from '../../../brunswick/containers/CollectionFilterQueryParamsContext'
import { isBrowser } from '../../utils'

// NextJS has cases where it flips back and forth on a query value before settling. Maybe this has to do with something
// we are causing, but I can't figure out what. So this stabalizes the value, only showing the final state query param.
function mergeNextQueryWithBrowserData(
  nextJsQueryParams: ParsedUrlQuery,
  nextJsPathnameWithSlugs: string
) {
  const queryParams: Record<string, string | string[] | undefined> = {
    ...nextJsQueryParams,
  }
  if (isBrowser()) {
    const browserQueryParams = new URLSearchParams(window.location.search)

    // Remove items not in the browser state
    Object.keys(queryParams).forEach(nextJsParamKey => {
      if (!nextJsPathnameWithSlugs.includes(`[${nextJsParamKey}]`)) {
        // If it is not a path param, we can sanitize it based on browser URL
        const browserQueryParamValue = browserQueryParams.get(nextJsParamKey)
        if (typeof browserQueryParamValue === 'undefined') {
          delete queryParams[nextJsParamKey]
        }
      }
    })

    // Pull in values from browser state
    browserQueryParams.forEach((val, key) => {
      if (typeof val === 'undefined') {
        delete queryParams[key]
      } else {
        queryParams[key] = val as string
      }
    })
  }

  return queryParams
}

export function usePagesDirectoryUpdateQueryParam(options?: UpdateQueryParamsOptions) {
  const { updateQueryParams } = usePagesDirectoryStableQueryParams(options)
  return (key: string, value: string | undefined) => {
    return updateQueryParams({ [key]: value })
  }
}

export function usePagesDirectoryStableQueryParams(
  options?: UpdateQueryParamsOptions
): UseQueryParams {
  const { query: routerQuery, replace, pathname: routerPathnameWithSlugs } = useRouter()

  const { shallow = true } = options ?? {}

  const routerPathname = useMemo(() => {
    let routerPathname = routerPathnameWithSlugs
    Object.entries(routerQuery).forEach(([key, value]) => {
      if (value) {
        routerPathname = routerPathname.replace(`[${key}]`, `${value}`)
      }
    })
    return routerPathname
  }, [routerPathnameWithSlugs, routerQuery])

  const stabalizedQuery = useMemo(() => {
    return mergeNextQueryWithBrowserData(routerQuery, routerPathnameWithSlugs)
  }, [routerQuery, routerPathnameWithSlugs])

  const updateQueryParams = useStableCallback(
    (updatedQueryParams: Record<string, string | undefined>) => {
      const updatedQuery = { ...stabalizedQuery }

      Object.entries(updatedQueryParams).forEach(([key, val]) => {
        if (typeof val === 'undefined') {
          delete updatedQuery[key]
        } else {
          updatedQuery[key] = val as string
        }
      })

      Object.keys(updatedQuery).forEach(key => {
        if (routerPathnameWithSlugs.includes(`[${key}]`)) {
          delete updatedQuery[key]
        }
      })

      replace(
        {
          // If we don't create and provide the router pathname,
          // we will get hard refreshes on actual browser urls
          // that are rewrites to NextJs urls. Like if they have
          // a `/en-US` prefix.
          pathname: routerPathname,
          query: updatedQuery,
        },
        undefined,
        { scroll: false, shallow }
      )
    }
  )

  return {
    query: stabalizedQuery,
    updateQueryParams,
  }
}
